import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
    MoreVertical,
    RefreshCw,
    Trash2,
    BarChart3,
    Edit,
    AlertCircle
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Widget, QueryResult } from '@/types';
import apiService from '@/services/api';
import ChartRenderer from '@/components/Charts/ChartRenderer';

interface WidgetCardProps {
    widget: Widget;
    dashboardId: string;
    onDelete?: (widgetId: string) => void;
    onUpdate?: (widgetId: string, updates: Partial<Widget>) => void;
}

const WidgetCard: React.FC<WidgetCardProps> = ({
    widget,
    onDelete
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<QueryResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async (forceRefresh: boolean = false) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await apiService.getWidgetData(widget.id, forceRefresh);
            setData(result);
        } catch (err: any) {
            console.error('Failed to fetch widget data:', err);
            setError(err.response?.data?.detail || 'Failed to load data');
        } finally {
            setIsLoading(false);
        }
    };

    // Initial data fetch
    useEffect(() => {
        fetchData();
    }, [widget.id, widget.sql_query]);

    const handleRefresh = () => {
        fetchData(true);
    };

    const handleDelete = () => {
        if (confirm('Are you sure you want to remove this widget?')) {
            onDelete?.(widget.id);
        }
    };

    const renderVisualization = () => {
        if (isLoading) {
            return (
                <div className="flex flex-col items-center justify-center h-full space-y-2">
                    <RefreshCw className="h-8 w-8 animate-spin text-primary opacity-50" />
                    <p className="text-xs text-muted-foreground">Fetching data...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className="flex flex-col items-center justify-center h-full text-destructive p-4 text-center">
                    <AlertCircle className="h-8 w-8 mb-2" />
                    <p className="text-sm font-medium">{error}</p>
                    <Button variant="ghost" size="sm" className="mt-2" onClick={() => fetchData(true)}>
                        <RefreshCw className="mr-2 h-3 w-3" /> Retry
                    </Button>
                </div>
            );
        }

        if (!data || !data.rows || data.rows.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4 text-center">
                    <BarChart3 className="h-12 w-12 mb-2 opacity-20" />
                    <p className="text-sm">No data returned</p>
                    <Button variant="outline" size="sm" className="mt-2" onClick={() => fetchData(true)}>
                        <RefreshCw className="mr-2 h-3 w-3" /> Refresh
                    </Button>
                </div>
            );
        }

        // Map WidgetType to ChartType if needed, but Widget has viz_config
        // Re-using ChartRenderer which expects:
        // chartType: ChartType;
        // columns: string[];
        // rows: any[][];
        // vizConfig?: any;

        return (
            <div className="h-full w-full overflow-hidden">
                <ChartRenderer
                    chartType={widget.viz_config?.chart_type || (widget.type as any)}
                    columns={data.columns}
                    rows={data.rows}
                    rowCount={data.row_count}
                    vizConfig={widget.viz_config}
                    height="100%"
                />
            </div>
        );
    };

    return (
        <Card className="h-full flex flex-col shadow-sm border-none bg-transparent">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0 px-2 pt-2">
                <CardTitle className="text-xs font-semibold text-foreground/80 line-clamp-1">
                    {widget.title}
                </CardTitle>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-6 w-6 p-0 hover:bg-muted/50">
                            <MoreVertical className="h-3 w-3 text-muted-foreground" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={handleRefresh}>
                            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled>
                            <Edit className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleDelete} className="text-red-500">
                            <Trash2 className="mr-2 h-4 w-4" /> Remove
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </CardHeader>
            <CardContent className="flex-1 min-h-0 p-0 overflow-hidden">
                {renderVisualization()}
            </CardContent>
        </Card>
    );
};

export default React.memo(WidgetCard);
